import React, { useCallback } from 'react';
import { Input } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';

import { MAGNIFYING_GLASS } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

// The clear button names what it clears (antd's own is "close-circle").
const CLEAR = { clearIcon: <CloseCircleFilled aria-label='Clear search box' /> };

/**
 * Focus a list's search box by its id (the `/` key: platform-standards
 * section 5). Returns whether there was a box to focus.
 */
export function focusListSearch(id) {
	const input = id ? document.getElementById(id) : null;
	if (!input || typeof input.focus !== 'function') return false;
	input.focus();
	if (typeof input.select === 'function') input.select();
	return true;
}

/**
 * The search box of a list of items (KPR-740): the collection list's and each
 * related list's, in the toolbar's `search` slot (ItemsListToolbar). It draws
 * what `useListSearch` holds and decides nothing:
 *
 *   <ListSearchBox search={useListSearch({ query, setQuery })} id='items-search' />
 *
 * - No autocorrect, capitalisation or spellcheck (field-assist ruling): a
 *   search term is a query, and autocorrect rewrites it when focus leaves.
 * - **Esc clears, then leaves.** With text in the box it clears the search and
 *   keeps focus; with the box empty it gives focus back to the page. Both stop
 *   there: an Esc that clears a search must not also cancel the form it sits in
 *   or close the pane beside it.
 * - What the server said about a refused term sits under the box, announced,
 *   and the box shows the error state until the person edits it.
 */
export default function ListSearchBox({
	search,
	id,
	label = 'Search items',
	placeholder = 'Search items…',
	className = '',
}) {
	const noticeId = id ? `${id}-notice` : undefined;
	const { text, notice, clear, onChange, onCompositionStart, onCompositionEnd, onPressEnter } = search;

	const onKeyDown = useCallback((event) => {
		if (event.key !== 'Escape' || event.nativeEvent?.isComposing) return;
		event.preventDefault();
		event.stopPropagation();
		if (event.currentTarget.value) clear();
		else event.currentTarget.blur();
	}, [clear]);

	return (
		<div className={`list-search${className ? ` ${className}` : ''}`}>
			<Input
				{...NO_TEXT_ASSIST}
				id={id}
				className='list-search__input'
				allowClear={CLEAR}
				prefix={<MAGNIFYING_GLASS aria-hidden='true' />}
				placeholder={placeholder}
				aria-label={label}
				aria-invalid={notice ? true : undefined}
				aria-describedby={notice ? noticeId : undefined}
				status={notice ? 'error' : undefined}
				value={text}
				onChange={onChange}
				onCompositionStart={onCompositionStart}
				onCompositionEnd={onCompositionEnd}
				onPressEnter={onPressEnter}
				onKeyDown={onKeyDown}
			/>
			{notice ? (
				<div id={noticeId} className='list-search__notice' role='alert'>{notice.message}</div>
			) : null}
		</div>
	);
}
